import { authenticate, handleForgotPassword, isAuthenticationAction } from "../prototypeActions";
import type { PrototypeState } from "../controllerState";
import type { Root } from "../prototypeTypes";
import {
  clearConfirmedLoginState,
  hideLoginState,
  showLoginState,
  validateModalLogin,
  validateModalRegister,
} from "../authGateController";
import { applyInitialViewFromUrl, closeAllModals, rehydrateCurrentView } from "../navigationController";
import { hideVisitorCatalogCtas } from '../visitorCatalogCta';
import { notifyError, recordRuntimeError } from "../prototypeRuntimeSupport";
import { invalidateTopicIndex } from "../topicStore";
import { syncTopicViewerCacheScope } from '../topicViewerCacheScope';
import { hydrateActiveHome } from '../homeViewHydrator';
import { setAuthButtonLoading } from "../actions/authButtonLock";
import { byId } from "../domUtils";
import { handleEmailVerificationResend } from '../actions/emailVerificationResendAction';
import { announceAuthSessionChange } from '../authSessionSync';
import { handleOAuthLinkCancel, handleOAuthLinkComplete, handleOAuthLinkForgotPassword } from '../actions/oauthLinkActions';
import { handleResetPassword } from '../actions/resetPasswordAction';
import { validateCapturedPasswordResetToken } from '../resetPasswordPreflight';
import { parseClientPlanContext } from '../clientPlanContext';
import { bindPendingRedeemIntentToUser } from '../pendingRedeemIntent';
import { feedbackTicketIdFromBrowserLocation } from '../notificationActionRoute';
import { activateFeedbackTab, loadMyFeedback, openFeedbackDetail, switchToFeedbackTab } from './profileFeedbackTicketActions';
import { deactivateHomeMember, hydrateHomeMemberLayout } from '../homeMemberHydrator';

/**
 * Resolve the canonical control that owns the pending interaction so it can
 * show a loading spinner during the 1-2s API round-trip.
 */
export function resolveAuthButton(action: string, root: Root): HTMLButtonElement | null {
  if (action.includes("SocialLogin")) {
	return (
		root.querySelector<HTMLButtonElement>(".modal-overlay.active #loginGoogleBtn") ??
		root.querySelector<HTMLButtonElement>(".modal-overlay.active #registerGoogleBtn")
    );
  }
  if (action.includes("Register")) return root.querySelector<HTMLButtonElement>("#registerSubmitBtn");
  return root.querySelector<HTMLButtonElement>("#loginSubmitBtn");
}

/**
 * Social login has one serialized action for both dialogs. Its owning dialog
 * must therefore come from the active control, not from the action text.
 */
function resolveAuthSurface(action: string, submitBtn: HTMLButtonElement | null): { modalId: 'loginModal' | 'registerModal'; alertId: 'loginFormAlert' | 'registerFormAlert' } {
	if (action.includes('SocialLogin')) {
		const modalId = submitBtn?.closest?.('.modal-overlay')?.id;
		if (modalId === 'registerModal') return { modalId, alertId: 'registerFormAlert' };
		return { modalId: 'loginModal', alertId: 'loginFormAlert' };
	}
	return action.includes('Register')
		? { modalId: 'registerModal', alertId: 'registerFormAlert' }
		: { modalId: 'loginModal', alertId: 'loginFormAlert' };
}

export interface AuthRouteDependencies {
  navigateHome?: (path: string) => void;
  announceSessionChange?: () => void;
	hydrateActiveHome?: (root: Root, state: PrototypeState) => Promise<void>;
  notifyError?: (message: string) => void;
}

const logoutPendingRoots = new WeakSet<object>();
const logoutLabelRestore = new WeakMap<HTMLElement, string>();

/** Lets delayed member mutations stop before creating a request during logout. */
export function isLogoutPending(root: Root): boolean {
	return logoutPendingRoots.has(root);
}

function setLogoutPending(root: Root, pending: boolean): void {
	const controls = Array.from(root.querySelectorAll<HTMLElement>('[data-proto-click="handleLogout()"]'));
	for (const control of controls) {
		const label = control.querySelector<HTMLElement>('.item-label, .av-label');
		if (pending) {
			control.dataset.logoutPending = 'true';
			control.setAttribute('aria-busy', 'true');
			control.setAttribute('aria-disabled', 'true');
			if (label && !logoutLabelRestore.has(label)) {
				logoutLabelRestore.set(label, label.textContent ?? 'Sign Out');
				label.textContent = 'Signing out…';
			}
			continue;
		}
		delete control.dataset.logoutPending;
		control.removeAttribute('aria-busy');
		control.removeAttribute('aria-disabled');
		if (label) {
			const previousLabel = logoutLabelRestore.get(label);
			if (previousLabel !== undefined) {
				label.textContent = previousLabel;
				logoutLabelRestore.delete(label);
			}
		}
	}
}

export async function routeAuthAction(
  normalized: string,
  root: Root,
  state: PrototypeState,
  replayPending?: () => Promise<void>,
  dependencies: AuthRouteDependencies = {},
) {
  if (normalized.includes('handleOAuthLinkComplete')) { await handleOAuthLinkComplete(root, state); return true; }
  if (normalized.includes('handleOAuthLinkForgotPassword')) { await handleOAuthLinkForgotPassword(root, state); return true; }
  if (normalized.includes('handleOAuthLinkCancel')) { await handleOAuthLinkCancel(root, state); return true; }
  // Exact-match the logout call. A loose `includes("handleLogout")`
  // substring match risked firing on any action whose serialized form
  // happened to contain that token (e.g. a future action named
  // `handleLogoutCancel`), which would silently log the user out.
  // The canonical action is `handleLogout()`; we also accept the bare
  // `handleLogout` form defensively. If the diagnostic ever logs a
  // different action string, we've found a new culprit.
  if (normalized === "handleLogout()" || normalized === "handleLogout") {
    if (normalized !== "handleLogout()") {
      console.warn("[auth] logout triggered by non-canonical action:", normalized);
    }
    if (logoutPendingRoots.has(root)) return true;
		const logoutViewerScope = `${state.runtime.userId}|${state.runtime.sessionHash}`;
    logoutPendingRoots.add(root);
		// Member enhancements may be waiting for their initial Home inputs. Stop
		// that second stage before the asynchronous logout revokes the cookie.
		deactivateHomeMember();
    setLogoutPending(root, true);
    try {
      await state.runtime.api.logout();
    } catch (error) {
      recordRuntimeError(state.runtime, error);
      (dependencies.notifyError ?? notifyError)('Sign out failed. Please try again.');
      setLogoutPending(root, false);
      logoutPendingRoots.delete(root);
		// The request was not revoked, so restore only the cancelled member
		// layout when the same authenticated viewer remains on Home. A concurrent
		// session-expiry handler may already have cleared this identity while the
		// logout transport was pending.
		if (state.isLoggedIn && `${state.runtime.userId}|${state.runtime.sessionHash}` === logoutViewerScope && root.querySelector('#view-home.active')) {
			void hydrateHomeMemberLayout(root, state.runtime.api).catch(() => undefined);
		}
      return true;
    }
    // The server has revoked the session. Clear identity-bound in-memory
    // state, but do not repaint the current Profile view as a visitor: doing
    // so creates a visible, incorrect category empty state just before the
    // full-page navigation below.
    clearConfirmedLoginState(state);
    invalidateTopicIndex();
    state.runtime.activeCourseId = "";
    (dependencies.announceSessionChange ?? announceAuthSessionChange)();
    (dependencies.navigateHome ?? ((path) => window.location.replace(path)))('/');
    return true;
  }
	if (normalized === 'handleResendVerification()' || normalized === 'handleResendVerification') {
		await handleEmailVerificationResend(root, state.runtime);
		return true;
	}
	if (normalized === 'retryResetPasswordValidation()' || normalized === 'retryResetPasswordValidation') {
		await validateCapturedPasswordResetToken(root, state);
		return true;
	}
	if (isAuthenticationAction(normalized)) {
		const submitBtn = resolveAuthButton(normalized, root);
		const { alertId, modalId } = resolveAuthSurface(normalized, submitBtn);
		let attemptStarted = false;
		let isCurrentAttempt = () => false;
		let canWriteAttemptUi = () => false;
		const failAuthValidation = (message: string) => !attemptStarted || canWriteAttemptUi()
			? paintAuthFormAlert(root, alertId, message)
			: false;
		clearAuthFormAlert(root, alertId);
		if (normalized.includes("handleLogin") && !validateModalLogin(root, failAuthValidation)) return true;
		if (normalized.includes("handleRegister") && !validateModalRegister(root, failAuthValidation)) return true;
		const originalText = submitBtn?.textContent ?? undefined;
		const loadingText = normalized.includes('SocialLogin')
			? 'Connecting…'
			: normalized.includes('Register')
				? 'Creating account…'
				: 'Signing in…';
		await authenticate(root, state.runtime, normalized, {
      hideLoginState: (targetRoot) => hideLoginState(targetRoot, state),
      closeAllModals,
      failValidation: failAuthValidation,
      recordRuntimeError,
      onAuthSuccess: (session) => {
		if (!isCurrentAttempt()) return;
		// `authenticate` has confirmed the server session at this point. Keep
		// controller state in sync before cache scoping and Home hydration; the
		// latter is deliberately guarded by `state.isLoggedIn` below.
		state.isLoggedIn = true;
		const planContext = parseClientPlanContext(session.viewer.planContext);
		state.planContext = planContext;
		state.currentPlan = planContext?.billingPlan ?? 'free';
		state.effectiveAccessPlan = planContext?.effectiveAccessPlan ?? 'free';
		state.runtime.currentPlan = state.effectiveAccessPlan;
		hideVisitorCatalogCtas(root);
        if (session.viewer.userId) state.runtime.userId = session.viewer.userId;
		state.runtime.sessionHash = session.viewer.sessionHash;
		if (session.viewer.userId) bindPendingRedeemIntentToUser(session.viewer.userId);
        if (session.viewer.email) state.runtime.userEmail = session.viewer.email;
        if (session.viewer.displayName) state.runtime.userDisplayName = session.viewer.displayName;
		syncTopicViewerCacheScope(state.runtime, true);
        invalidateTopicIndex();
      },
	  isAttemptCurrent: () => isCurrentAttempt(),
	  canWriteUi: () => canWriteAttemptUi(),
	  onSubmitStart: () => {
		attemptStarted = true;
		const attempt = beginAuthAttempt(root, submitBtn, modalId);
		isCurrentAttempt = attempt.isCurrent;
		canWriteAttemptUi = attempt.canWriteUi;
		setAuthButtonLoading(submitBtn, true, originalText, loadingText);
	  },
	  onSubmitEnd: () => {
		if (canWriteAttemptUi()) setAuthButtonLoading(submitBtn, false, originalText);
	  },
    });
	if (!isCurrentAttempt()) return true;
    if (state.isLoggedIn) {
      (dependencies.announceSessionChange ?? announceAuthSessionChange)();
		await (dependencies.hydrateActiveHome ?? hydrateActiveHome)(root, state);
		// A visitor can arrive from the durable Feedback reply-email CTA. The
		// initial route deliberately shows only the login modal, so success must
		// re-apply the exact, validated URL and then open its owned conversation.
		// Without this branch the account was authenticated but stranded on the
		// generic Feedback form instead of returning to the support thread.
		const feedbackTicketId = feedbackTicketIdFromBrowserLocation(window.location);
		if (feedbackTicketId) {
			applyInitialViewFromUrl(root, state);
			switchToFeedbackTab(root, 'my');
			activateFeedbackTab(root, 'my');
			await loadMyFeedback(root, state);
			await openFeedbackDetail(root, state, feedbackTicketId);
		} else if (window.location.pathname.replace(/\/+$/u, '') === '/membership-billing') {
			// A visitor deep link deliberately stays on Home behind the login
			// modal. Once authentication succeeds, re-apply that durable route so
			// the original Membership & Billing destination becomes active without
			// adding history or refreshing the document.
			applyInitialViewFromUrl(root, state);
		} else {
			// Re-render whichever view the user was on when they opened the
			// login modal. Without this, Profile / Browse / Community / etc.
			// keep showing stale visitor-state DOM after a successful login.
			rehydrateCurrentView(root);
		}
		await replayPending?.();
    }
    return true;
  }
  if (normalized.includes("handleForgotPassword")) {
    clearAuthFormAlert(root, 'forgotFormAlert');
    await handleForgotPassword(root, state.runtime, {
      failValidation: (message) => paintAuthFormAlert(root, 'forgotFormAlert', message),
      recordRuntimeError,
    });
    return true;
  }
  if (normalized.includes("handleResetPassword")) {
    await handleResetPassword(root, state.runtime);
    return true;
  }
  return false;
}

function beginAuthAttempt(root: Root, button: HTMLButtonElement | null, modalId: string): { isCurrent: () => boolean; canWriteUi: () => boolean } {
	if (!button) return { isCurrent: () => false, canWriteUi: () => false };
	const token = crypto.randomUUID();
	button.dataset.uiAttempt = token;
	const isCurrent = () => button.dataset.uiAttempt === token;
	return { isCurrent, canWriteUi: () => isCurrent() && root.querySelector(`#${modalId}`)?.classList.contains('active') === true };
}

function clearAuthFormAlert(root: Root, id: string): void {
  const alert = byId(root, id);
  if (!alert) return;
  alert.textContent = '';
  alert.hidden = true;
}

function paintAuthFormAlert(root: Root, id: string, message: string): false {
	const alert = byId(root, id);
	if (alert) {
		alert.textContent = message;
		alert.hidden = false;
	}
	// Auth dialogs already own an assertive inline error region. Mirroring the
	// same validation in a viewport-level Toast creates a second prompt on
	// mobile and can visually cover the first field that needs attention.
	return false;
}
